import React, { useEffect, useState, useRef } from 'react';
import { formatValueWithDecimals } from '../../utils/numberFormatter';

/**
 * TextLabelRenderer 컴포넌트
 * 텍스트 라벨을 컨테이너에 맞게 자동 크기 조정하여 렌더링하는 컴포넌트
 * 
 * @param {Object} chartData - 차트 데이터 (표준 구조)
 * @param {string} [chartData.viz_type] - 'text_label'
 * @param {Object} [chartData.options] - 옵션
 * @param {Object} error - 에러 객체
 * @param {boolean} isEditMode - 편집 모드 여부
 */
const TextLabelRenderer = ({ chartData, error, isEditMode = false, isSelected = false }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [optimalFontSize, setOptimalFontSize] = useState(35);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isCalculating, setIsCalculating] = useState(false);
  
  const options = chartData?.options || {};
  const rawContent = options.content || '텍스트를 입력하세요';

  // 숫자 포맷팅 처리 (소수점 자리수 옵션 적용)
  const formatContent = () => {
    const decimalPlaces = options.advanced?.decimalPlaces ?? null;
    // 숫자인지 확인하고 포맷 적용
    const numValue = parseFloat(rawContent);
    if (!isNaN(numValue) && typeof rawContent !== 'string' || (typeof rawContent === 'string' && !isNaN(numValue))) {
      // rawContent가 숫자 형태의 문자열이거나 숫자인 경우
      if (rawContent.toString() === numValue.toString() || rawContent.toString() === numValue.toFixed(1) || rawContent.toString() === numValue.toFixed(2)) {
        return formatValueWithDecimals(numValue, decimalPlaces);
      }
    }
    // 그 외의 경우 원본 그대로 반환
    return rawContent;
  };

  const content = formatContent();
  
  /**
   * 텍스트의 실제 렌더링 크기를 측정하는 함수
   * @param {string} text - 측정할 텍스트
   * @param {number} fontSize - 폰트 크기
   * @param {Object} textStyle - 텍스트 스타일
   * @returns {Object} { width, height } - 텍스트의 실제 크기
   */
  const measureTextSize = (text, fontSize, textStyle) => {
    if (!measureRef.current) return { width: 0, height: 0 };
    
    measureRef.current.style.fontSize = `${fontSize}px`;
    measureRef.current.style.fontFamily = textStyle.fontFamily;
    measureRef.current.style.fontWeight = textStyle.fontWeight;
    measureRef.current.style.fontStyle = textStyle.fontStyle;
    measureRef.current.style.lineHeight = textStyle.lineHeight;
    measureRef.current.style.whiteSpace = 'pre-wrap';
    measureRef.current.style.wordBreak = 'break-word';
    measureRef.current.textContent = text;
    
    const rect = measureRef.current.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  };
  
  /**
   * 이진 탐색을 통해 컨테이너에 맞는 최적 폰트 크기를 찾는 함수
   * @param {number} containerWidth - 컨테이너 너비
   * @param {number} containerHeight - 컨테이너 높이
   * @param {string} text - 텍스트 내용
   * @param {Object} textStyle - 텍스트 스타일
   * @returns {number} 최적 폰트 크기
   */
  const findOptimalFontSize = (containerWidth, containerHeight, text, textStyle) => {
    if (!containerWidth || !containerHeight || !text) return 35;
    
    // 여백을 고려한 실제 사용 가능한 크기 계산 - 패딩을 더 줄임
    const padding = Math.max(1, Math.min(containerWidth, containerHeight) * 0.02); // 5%에서 2%로 줄임
    const availableWidth = containerWidth - padding;
    const availableHeight = containerHeight - padding;
    
    if (availableWidth <= 0 || availableHeight <= 0) return 6;
    
    // 매우 작은 컨테이너를 위한 최소 폰트 크기 조정
    let minSize = Math.max(4, Math.min(6, Math.min(availableWidth, availableHeight) / 5));
    let maxSize = Math.min(availableWidth, availableHeight);
    let bestSize = minSize;
    
    // 이진 탐색으로 최적 크기 찾기
    while (minSize <= maxSize) {
      const midSize = Math.floor((minSize + maxSize) / 2);
      const textSize = measureTextSize(text, midSize, textStyle);
      
      // 텍스트가 컨테이너에 맞는지 확인 (더 넉넉하게 검사)
      if (textSize.width <= availableWidth * 0.98 && textSize.height <= availableHeight * 0.95) {
        bestSize = midSize;
        minSize = midSize + 1; // 더 큰 크기 시도
      } else {
        maxSize = midSize - 1; // 더 작은 크기 시도
      }
    }
    
    return Math.max(4, bestSize); // 절대 최소값 4px 보장
  };
  
  // 컨테이너 크기 변화에 따른 최적 폰트 크기 계산
  useEffect(() => {
    const calculateOptimalSize = () => {
      if (!containerRef.current || !measureRef.current) return;
      
      setIsCalculating(true);
      
      const container = containerRef.current;
      const { width, height } = container.getBoundingClientRect();
      
      setContainerSize({ width, height });
      
      // 컨테이너 크기에 따른 동적 lineHeight 계산 (측정용)
      const containerArea = width * height;
      let dynamicLineHeight = '1.4';
      if (containerArea < 15000) {
        dynamicLineHeight = '1.0';
      } else if (containerArea < 30000) {
        dynamicLineHeight = '1.2';
      } else {
        dynamicLineHeight = options.lineHeight || '1.4';
      }
      
      // 텍스트 스타일 준비
      const textStyle = {
        fontFamily: options.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontWeight: options.fontWeight || 'normal',
        fontStyle: options.fontStyle || 'normal',
        lineHeight: dynamicLineHeight
      };
      
      // 최적 폰트 크기 계산
      const optimalSize = findOptimalFontSize(width, height, content, textStyle);
      setOptimalFontSize(optimalSize);
      
      setIsCalculating(false);
    };
    
    // 초기 계산
    calculateOptimalSize();
    
    // ResizeObserver로 컨테이너 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      calculateOptimalSize();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    // 윈도우 리사이즈 이벤트 추가
    const handleWindowResize = () => {
      setTimeout(calculateOptimalSize, 100);
    };
    
    window.addEventListener('resize', handleWindowResize);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [content, options.fontFamily, options.fontWeight, options.fontStyle, options.lineHeight]);
  
  // 정렬 옵션 처리
  const verticalAlign = options.verticalAlign || 'center';
  const horizontalAlign = options.textAlign || 'center';
  
  // 컨테이너 크기에 따른 동적 lineHeight 계산
  const getDynamicLineHeight = () => {
    const baseLineHeight = parseFloat(options.lineHeight || '1.4');
    const containerArea = containerSize.width * containerSize.height;
    
    // 매우 작은 컨테이너 (15000px² 미만)에서는 lineHeight를 줄임
    if (containerArea < 15000) {
      return '1.0';
    } else if (containerArea < 30000) {
      return '1.2';
    }
    return baseLineHeight.toString();
  };
  
  // flexbox 정렬 값 변환
  const getFlexAlignment = () => {
    const justifyContent = (() => {
      switch(verticalAlign) {
        case 'flex-start': return 'flex-start';
        case 'flex-end': return 'flex-end';
        case 'center':
        default: return 'center';
      }
    })();
    
    const alignItems = (() => {
      switch(horizontalAlign) {
        case 'left': return 'flex-start';
        case 'right': return 'flex-end';
        case 'center':
        default: return 'center';
      }
    })();
    
    return { justifyContent, alignItems };
  };
  
  const flexAlignment = getFlexAlignment();
  
  // 단순한 텍스트 스타일 설정
  // 대비 색상 계산 유틸
  const getContrastingTextColor = (bg) => {
    if (!bg || bg === 'transparent') return (options.textColor || '#333333');
    // #rrggbb 형식만 간단 지원
    try {
      const hex = bg.replace('#','');
      const r = parseInt(hex.substring(0,2),16);
      const g = parseInt(hex.substring(2,4),16);
      const b = parseInt(hex.substring(4,6),16);
      // WCAG 상대휘도 기반 간이 판단
      const luminance = (0.2126*r + 0.7152*g + 0.0722*b)/255;
      return luminance > 0.55 ? '#111111' : '#FFFFFF';
    } catch (_) { return (options.textColor || '#333333'); }
  };

  const effectiveBg = options.backgroundColor || 'transparent';
  const selectedPillTint = options.style?.selected?.pillTint || 'rgba(0,0,0,0.08)';
  const selectedInvert = options.style?.selected?.invert !== false; // 기본 true

  const textStyle = {
    fontSize: `${optimalFontSize}px`,
    fontWeight: options.fontWeight || 'normal',
    fontStyle: options.fontStyle || 'normal',
    color: (isSelected && selectedInvert) ? getContrastingTextColor(effectiveBg === 'transparent' ? '#ffffff' : effectiveBg) : (options.textColor || '#333333'),
    textAlign: horizontalAlign,
    fontFamily: options.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    lineHeight: getDynamicLineHeight(),
    borderRadius: options.borderRadius || '0px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    textShadow: options.textShadow && options.textShadow !== 'none' ? options.textShadow : 'none',
    textDecoration: options.textDecoration || 'none',
    margin: 0,
    padding: '5px', // 최소한의 패딩
    boxSizing: 'border-box'
  };
  
  // 컨테이너 스타일 설정
  const containerStyle = {
    width: '100%', 
    height: '100%',
    display: 'flex',
    justifyContent: flexAlignment.justifyContent,
    alignItems: flexAlignment.alignItems,
    position: 'relative',
    overflow: 'hidden',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    backgroundColor: effectiveBg
  };
  
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#e74c3c',
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <strong>텍스트 라벨 오류:</strong> {error}
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      className="text-label-renderer"
      style={containerStyle}
    >
      {/* 텍스트 크기 측정을 위한 숨겨진 요소 */}
      <div 
        ref={measureRef}
        style={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          visibility: 'hidden',
          whiteSpace: 'nowrap'
        }}
      />
      
      <div className="text-label-content" style={textStyle}>
        {content}
      </div>
      {isSelected && selectedInvert && effectiveBg === 'transparent' && (
        <div style={{
          position: 'absolute',
          inset: '4px',
          borderRadius: '8px',
          background: selectedPillTint || 'rgba(74, 144, 226, 0.10)',
          boxShadow: 'inset 0 3px 8px rgba(0,0,0,0.18)',
          pointerEvents: 'none'
        }}/>
      )}
      
      {/* 편집 모드에서만 표시되는 편집 힌트 */}
      {isEditMode && (
        <>
          <div style={{
            position: 'absolute',
            top: '5px',
            right: '5px',
            fontSize: '11px',
            color: '#666',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '2px 6px',
            borderRadius: '3px',
            pointerEvents: 'none',
            zIndex: 10
          }}>
            텍스트 라벨
          </div>
          
          {/* 실시간 크기 디버그 정보 */}
          <div style={{
            position: 'absolute',
            bottom: '5px',
            left: '5px',
            fontSize: '9px',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            padding: '4px 6px',
            borderRadius: '3px',
            pointerEvents: 'none',
            zIndex: 10,
            fontFamily: 'monospace',
            lineHeight: '1.1',
            whiteSpace: 'pre'
          }}>
            {`컨테이너: ${Math.round(containerSize.width)}×${Math.round(containerSize.height)}
모드: ${isEditMode ? '편집' : '런타임'}
폰트: ${optimalFontSize}px | lineHeight: ${getDynamicLineHeight()}
flexbox: justify=${flexAlignment.justifyContent}, align=${flexAlignment.alignItems}
정렬설정: ${verticalAlign}, ${horizontalAlign}`}
          </div>
        </>
      )}
    </div>
  );
};

export default TextLabelRenderer; 